// =====================================================================================
// FILE: src/components/pickers/InlineAssetPicker.jsx
// =====================================================================================
// Simple Asset Picker - No virtualization, just shows all assets
// ✅ Uses Portal to render outside table container (prevents clipping)
// ✅ Smart positioning: Opens upward when near bottom of viewport
// ✅ Filters assets by column destination
// =====================================================================================

import React, { useState, useRef, useEffect, useMemo, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { THEME, FONTS } from "../../constants/theme";
import { filterAssetsByColumn } from "../../constants/assetDestinations";

export default function InlineAssetPicker({
  current,
  disabled,
  assetCache,
  onPick,
  onClear,
  onClose,
  columnKey,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef();
  const pickerRef = useRef();
  const anchorRef = useRef();
  
  // ✅ Use refs for position to avoid re-renders
  const positionRef = useRef({ top: 0, left: 0, openUpward: false, width: 400 });
  const [ready, setReady] = useState(false);

  // ✅ Convert assetCache to array
  const allAssets = useMemo(() => {
    if (!assetCache) return [];
    if (assetCache instanceof Map) return Array.from(assetCache.values());
    if (typeof assetCache === 'object') return Object.values(assetCache);
    return [];
  }, [assetCache]);

  // ✅ Filter assets by column destination and exclude archived
  const assets = useMemo(() => {
    // First exclude archived assets
    let filtered = allAssets.filter(asset => !asset.archived);
    // Then filter by column destination
    if (!columnKey) return filtered;
    return filterAssetsByColumn(filtered, columnKey);
  }, [allAssets, columnKey]);

  // ✅ Filter assets by search and sort newest first, with current selection at top
  const filteredAssets = useMemo(() => {
    let result = assets;
    
    // Filter by search term
    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      result = result.filter((asset) => {
        const title = (asset.title || "").toLowerCase();
        const destination = (asset.destination || "").toLowerCase();
        const id = (asset.id || "").toLowerCase();
        const lumenId = (asset.lumenId || "").toLowerCase(); // ✅ Search by Lumen8 ID (e.g., STK_ABC123)
        return title.includes(lower) || destination.includes(lower) || id.includes(lower) || lumenId.includes(lower);
      });
    }
    
    // ✅ Sort by created time (newest first)
    const sorted = [...result].sort((a, b) => {
      // If we have createdTime, use it
      if (a.createdTime && b.createdTime) {
        return new Date(b.createdTime) - new Date(a.createdTime);
      }
      // Fallback: sort by ID (Airtable IDs are roughly chronological)
      return (b.id || "").localeCompare(a.id || "");
    });
    
    // ✅ Move current selection to top (if it exists in the list)
    if (current?.id) {
      const currentIndex = sorted.findIndex(a => a.id === current.id);
      if (currentIndex > 0) {
        const [currentAsset] = sorted.splice(currentIndex, 1);
        sorted.unshift(currentAsset);
      }
    }
    
    return sorted;
  }, [assets, searchTerm, current]);

  // ✅ Calculate position ONCE using useLayoutEffect (sync, before paint)
  useLayoutEffect(() => {
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 450;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
      
      positionRef.current = {
        top: openUpward ? rect.top - 4 : rect.bottom + 4,
        left: rect.left,
        openUpward,
        width: Math.max(400, rect.width),
      };
      setReady(true);
    }
  }, []);

  // ✅ Auto-focus search after ready
  useEffect(() => {
    if (ready && searchInputRef.current && !disabled) {
      searchInputRef.current.focus();
    }
  }, [ready, disabled]);

  // ✅ Click outside to close - with longer delay to prevent accidental closes
  useEffect(() => {
    let isActive = false;
    
    // Longer delay to prevent immediate closing when opening or during search
    const timer = setTimeout(() => {
      isActive = true;
    }, 300);
    
    const handleClickOutside = (event) => {
      if (!isActive) return; // Ignore clicks during initial delay
      // Don't close if clicking inside the picker or anchor
      if (pickerRef.current && pickerRef.current.contains(event.target)) return;
      if (anchorRef.current && anchorRef.current.contains(event.target)) return;
      onClose();
    };
    
    // Use mousedown instead of click for more predictable behavior
    document.addEventListener("mousedown", handleClickOutside);
    
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  // ✅ Escape to close
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const pos = positionRef.current;

  // ✅ Dropdown content
  const dropdownContent = ready ? (
    <div
      ref={pickerRef}
      onMouseDown={(e) => e.stopPropagation()} // Prevent any mousedown from bubbling
      onClick={(e) => e.stopPropagation()} // Prevent any click from bubbling
      style={{
        position: "fixed",
        top: pos.openUpward ? "auto" : pos.top,
        bottom: pos.openUpward ? (window.innerHeight - pos.top) : "auto",
        left: pos.left,
        zIndex: 99999,
        background: THEME.surface,
        border: `1px solid ${THEME.line}`,
        borderRadius: THEME.radiusMd,
        boxShadow: THEME.shadowXl,
        minWidth: 400,
        maxWidth: 500,
        width: pos.width,
        maxHeight: 450,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        fontFamily: FONTS.family,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "12px 16px",
          borderBottom: `1px solid ${THEME.line}`,
          background: THEME.surface,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <span style={{ fontSize: FONTS.sm, fontWeight: FONTS.semibold, color: THEME.text }}>
            Select Asset
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            style={{
              border: "none",
              background: "transparent",
              fontSize: 20,
              cursor: "pointer",
              padding: 0,
              color: THEME.textSecondary,
              lineHeight: 1,
            }}
            title="Close (Esc)"
          >
            ×
          </button>
        </div>

        {/* Search */}
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search assets..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onMouseDown={(e) => e.stopPropagation()} // Prevent triggering click-outside
          onClick={(e) => e.stopPropagation()} // Prevent triggering click-outside
          disabled={disabled}
          style={{
            width: "100%",
            padding: "8px 12px",
            border: `1px solid ${THEME.line}`,
            borderRadius: THEME.radius,
            fontSize: FONTS.sm,
            fontWeight: FONTS.medium,
            fontFamily: FONTS.family,
            outline: "none",
            boxSizing: "border-box",
          }}
        />
      </div>

      {/* Asset count */}
      <div style={{ 
        padding: "8px 16px", 
        fontSize: FONTS.xs, 
        color: THEME.textSecondary,
        borderBottom: `1px solid ${THEME.lineLight}`,
        fontWeight: FONTS.medium,
      }}>
        {filteredAssets.length} asset{filteredAssets.length !== 1 ? "s" : ""}
      </div>

      {/* Asset List */}
      <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
        {/* Clear option */}
        {current && (
          <div
            onMouseDown={(e) => e.stopPropagation()} // Prevent triggering click-outside
            onClick={(e) => { 
              e.stopPropagation(); // Prevent triggering click-outside handler
              onClear(); 
            }}
            style={{
              padding: "10px 16px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 10,
              borderBottom: `1px solid ${THEME.lineLight}`,
              color: THEME.error,
              fontWeight: FONTS.medium,
              fontSize: FONTS.sm,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = THEME.errorLight; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            ✕ Clear selection
          </div>
        )}

        {filteredAssets.length === 0 ? (
          <div style={{ padding: "20px 16px", textAlign: "center", color: THEME.textSecondary, fontSize: FONTS.sm }}>
            No assets found
          </div>
        ) : (
          filteredAssets.map((asset) => (
            <div
              key={asset.id}
              onMouseDown={(e) => e.stopPropagation()} // Prevent triggering click-outside
              onClick={(e) => {
                e.stopPropagation(); // Prevent triggering click-outside handler
                onPick(asset);
              }}
              style={{
                padding: "10px 16px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 12,
                background: current?.id === asset.id ? THEME.primaryLight : "transparent",
              }}
              onMouseEnter={(e) => { 
                if (current?.id !== asset.id) e.currentTarget.style.background = THEME.surface2; 
              }}
              onMouseLeave={(e) => { 
                e.currentTarget.style.background = current?.id === asset.id ? THEME.primaryLight : "transparent"; 
              }}
            >
              {/* Thumbnail */}
              {asset.thumb ? (
                <img
                  src={asset.thumb}
                  alt=""
                  style={{
                    width: 64,
                    height: 64,
                    objectFit: "contain",
                    borderRadius: THEME.radius,
                    background: THEME.surface2,
                    flexShrink: 0,
                  }}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : (
                <div style={{
                  width: 64,
                  height: 64,
                  borderRadius: THEME.radius,
                  background: THEME.surface2,
                  flexShrink: 0,
                }} />
              )}}

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontWeight: FONTS.semibold,
                  fontSize: FONTS.sm,
                  color: THEME.text,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}>
                  {asset.title || "(Untitled)"}
                </div>
                <div style={{
                  fontSize: FONTS.xs,
                  color: THEME.textSecondary,
                  marginTop: 2,
                }}>
                  {asset.destination || "—"}
                </div>
              </div>

              {/* Alpha/Audio indicators */}
              <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                {asset.hasAlpha && (
                  <span title="Has Alpha" style={{ fontSize: 14 }}>🎨</span>
                )}
                {asset.hasAudio && (
                  <span title="Has Audio" style={{ fontSize: 14 }}>🔊</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  ) : null;

  return (
    <>
      {/* Invisible anchor for position measurement */}
      <div ref={anchorRef} style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, pointerEvents: "none" }} />
      
      {/* Portal dropdown */}
      {createPortal(dropdownContent, document.body)}
    </>
  );
}
